/**
 * App Lifecycle Service
 * Centralizes handling of app state changes using @capacitor/app
 * Enables recording protection when app goes to background
 */

import { App, AppState } from '@capacitor/app';
import { PlatformService } from '@/utils/platform';

export type AppLifecycleState = 'active' | 'inactive' | 'background';
export type LifecycleHandler = (isActive: boolean, state: AppLifecycleState) => void;

class AppLifecycleService {
  private handlers: Set<LifecycleHandler> = new Set();
  private currentState: AppLifecycleState = 'active';
  private initialized = false;
  private listenerHandle: { remove: () => Promise<void> } | null = null;

  /**
   * Initialize the lifecycle service
   * Safe to call multiple times - will only initialize once
   */
  async initialize(): Promise<void> {
    if (this.initialized) {
      return;
    }

    // On web, we use document visibility API instead
    if (!PlatformService.isNative()) {
      this.initializeWebListeners();
      this.initialized = true;
      return;
    }

    try {
      // Native platforms use Capacitor App plugin
      this.listenerHandle = await App.addListener('appStateChange', (state: AppState) => {
        const isActive = state.isActive;
        const newState: AppLifecycleState = isActive ? 'active' : 'background';

        this.currentState = newState;
        this.notifyHandlers(isActive, newState);
      });

      this.initialized = true;
    } catch (error) {
      console.error('[AppLifecycle] Failed to initialize:', error);
      // Fallback to web listeners even on native if plugin fails
      this.initializeWebListeners();
      this.initialized = true;
    }
  }

  /**
   * Initialize web-based visibility listeners
   * Used for web platform or as fallback
   */
  private initializeWebListeners(): void {
    // Page visibility API - handles tab switching, minimizing
    document.addEventListener('visibilitychange', () => {
      const isActive = document.visibilityState === 'visible';
      const newState: AppLifecycleState = isActive ? 'active' : 'inactive';

      this.currentState = newState;
      this.notifyHandlers(isActive, newState);
    });

    // Page hide event - more reliable for detecting page unload
    window.addEventListener('pagehide', () => {
      this.currentState = 'background';
      this.notifyHandlers(false, 'background');
    });

    // Page show event - handles back/forward cache
    window.addEventListener('pageshow', () => {
      this.currentState = 'active';
      this.notifyHandlers(true, 'active');
    });

    // Blur/focus for window-level events
    window.addEventListener('blur', () => {
      // Only update to inactive if not already in background
      if (this.currentState === 'active') {
        this.currentState = 'inactive';
        this.notifyHandlers(false, 'inactive');
      }
    });

    window.addEventListener('focus', () => {
      this.currentState = 'active';
      this.notifyHandlers(true, 'active');
    });
  }

  /**
   * Notify all subscribed handlers of state change
   */
  private notifyHandlers(isActive: boolean, state: AppLifecycleState): void {
    this.handlers.forEach(handler => {
      try {
        handler(isActive, state);
      } catch (error) {
        console.error('[AppLifecycle] Handler error:', error);
      }
    });
  }

  /**
   * Subscribe to lifecycle changes
   * @returns Unsubscribe function
   */
  subscribe(handler: LifecycleHandler): () => void {
    this.handlers.add(handler);

    return () => {
      this.handlers.delete(handler);
    };
  }

  /**
   * Get current app state
   */
  getState(): AppLifecycleState {
    return this.currentState;
  }

  /**
   * Check if app is currently active (in foreground)
   */
  isActive(): boolean {
    return this.currentState === 'active';
  }

  /**
   * Check if app is in background
   */
  isBackground(): boolean {
    return this.currentState === 'background';
  }

  /**
   * Cleanup listeners (call when app is unmounting)
   */
  async cleanup(): Promise<void> {
    if (this.listenerHandle) {
      await this.listenerHandle.remove();
      this.listenerHandle = null;
    }
    this.handlers.clear();
    this.initialized = false;
  }
}

// Export singleton instance
export const appLifecycleService = new AppLifecycleService();
